<template>
  <div class="question-results-card">
    <full-screen-spinner v-if="initialLoading" />
    <chart-table-container
      v-else
      :title-left="$t('formResults.question')"
      :title-middle="$t('formResults.distribution')"
      :title-right="$t('formResults.score')"
    >
      <div
        v-for="(item, index) in chartList"
        :key="item.field.uid"
      >
        <m-content padding>
          <chart-row
            class="_row"
            :chart="item.chart"
            :chart-data="item.data"
            :title="item.field.title[lang]"
            :show-comments="item.field.allowComment && showComments"
            :max-value="getMaxValue(item.field)"
            :starts-from-zero="getStartsFromZero(item.field)"
            :title-link="link(item.field)"
          />
        </m-content>
        <m-divider
          v-if="index !== chartList.length - 1"
          none
        />
      </div>
    </chart-table-container>
  </div>
</template>

<script>
import ChartRow from '@/components/dashboard/ChartRow.vue';
import ChartTableContainer from '@/components/dashboard/ChartTableContainer.vue';
import FullScreenSpinner from 'shared/components/FullScreenSpinner.vue';
import useForm from '@/composables/form/form';
import { UserFilterHandler } from '@/lib/filter/user/handler';
import { chartType, fieldType } from 'shared/constants.json';
import { dataAggregation } from 'shared/num_constants.json';
import { getMinMaxOfField } from 'shared/lib/form-field';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'QuestionResultsCard',
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    filterTree: {
      type: Object,
      default: () => null,
    },
    intervalConfig: {
      type: Object,
      default: () => null,
    },
    showComments: {
      type: Boolean,
      default: false,
    },
    filterByNewestAnswer: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ChartTableContainer,
    ChartRow,
    FullScreenSpinner,
  },
  setup() {
    const { formId, formAvailable, lang } = useForm();
    return { formId, formAvailable, lang };
  },
  data() {
    return { initialLoading: false };
  },
  computed: {
    ...mapState({ answers: (state) => state.formResultData }),
    gqlFilter() {
      const handler = new UserFilterHandler(true);
      return handler.Translate(this.filterTree).filterTree;
    },
    chartList() {
      if (this.answers === null) {
        return [];
      }
      return this.fields.map((field) => {
        let data = this.answers[field.uid];
        if (typeof data === 'undefined') {
          data = null;
        }
        return {
          chart: this.chartByField(field),
          data,
          field,
        };
      }).filter((e) => e.data !== null);
    },
  },
  methods: {
    ...mapActions(['getFormResultData']),
    link(field) {
      if (this.routeFieldId() === field.uid) {
        return null;
      }
      return { ...this.$route, query: { fieldId: field.uid } };
    },
    routeFieldId() {
      const param = this.$route.query.fieldId;
      if (typeof param === 'undefined') {
        return 0;
      }
      return parseInt(param, 10);
    },
    getResultData() {
      if (!this.formAvailable) {
        return;
      }
      this.setLoading(true);
      this.getFormResultData({
        formId: this.formId,
        charts: this.chartsByFields(this.fields),
        userScopeTree: this.filterTree,
        intervalConfig: this.intervalConfig,
      }).then((response) => {
        if (response.status !== 200) {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
          return;
        }
        this.initialLoading = false;
        this.setLoading(false);
      });
    },
    setLoading(isLoading) {
      this.$store.commit('FORM_RESULT_SET_LOADING', isLoading);
    },
    chartsByFields(fields) {
      return fields.map((field) => this.chartByField(field));
    },
    chartsByForm(form) {
      return form.orderItems.map((item) => this.chartByField(item.field));
    },
    chartByField(field) {
      if (field.type === fieldType.longText) {
        return {
          uid: field.uid,
          dataFields: [field],
          type: chartType.freeText,
        };
      }
      return {
        uid: field.uid,
        dataFields: [field],
        aggregation: dataAggregation.favorability,
        calculateDistribution: true,
        type: chartType.stackedBarChart,
        filterByNewestAnswer: this.filterByNewestAnswer,
      };
    },
    getMaxValue(field) {
      const { max } = getMinMaxOfField(field);
      return max;
    },
    getStartsFromZero(field) {
      if (typeof field.opinionScaleProperties === 'undefined') {
        return false;
      }
      return field.opinionScaleProperties.startsFromZero;
    },
  },
  watch: {
    formId() {
      this.getResultData();
    },
    fields() {
      this.getResultData();
    },
    gqlFilter() {
      this.getResultData();
    },
    intervalConfig() {
      this.getResultData();
    },
  },
  created() {
    this.initialLoading = true;
    this.getResultData();
  },
};
</script>

<style scoped lang="scss" type="text/scss">

</style>
